import { type ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Route, Switch, useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';

import type * as graphql from '@treams/treams-network';
import { type ITimeMachine, Separator, Tabs, tokens } from '@treams/treams-ui';

import type { GroupSelectionData } from '../goals/group-selector/new/useGroupSelection';
import { AnalyticsRefreshButton, AnalyticsSyncStatus } from './AnalyticsRefreshButton';
import Metric from './Metric';
import { CycleAnalytics } from './cycles/CycleAnalytics';
import { RewardAnalytics } from './reward/RewardAnalytics';
import { TalentReviewAnalytics } from './talent-review/TalentReviewAnalytics';
import { TreamsScoreAnalytics } from './treams-score/TreamsScoreAnalytics';

export enum AnalyticsRoute {
	Overview = '/analytics',
	Cycles = '/analytics/cycles',
	TalentReview = '/analytics/talent-review',
	TreamsScore = '/analytics/treams-score',
	Reward = '/analytics/reward',
	Metric = '/analytics/metric/:metricGroupID/:metricID/:tabID?',
}

export const ANALYTICS_VIEW_KEY = 'view';
export const ANALYTICS_VIEW_VALUE_ENGAGEMENT = 'engagement';
export const ANALYTICS_VIEW_VALUE_OTHER = 'all';

export function AnalyticsHeaderTabs(props: {
	isEngagementOfficer: boolean;
	hasCycleAnalytics: boolean;
	hasTalentReviewAnalytics: boolean;
	hasTreamsScore: boolean;
	hasRewards: boolean;
	isInEngagementAnalytics: boolean;
}) {
	const { t } = useTranslation();
	const location = useLocation();
	const history = useHistory();

	const onClickViewTab = useCallback(
		(newTabValue: string) => {
			const searchParams = new URLSearchParams(location.search);
			searchParams.set(ANALYTICS_VIEW_KEY, newTabValue);
			history.push({
				pathname: AnalyticsRoute.Overview,
				search: searchParams.toString(),
			});
		},
		[location.search, history],
	);

	const onSnapshotPage =
		location.pathname.startsWith(AnalyticsRoute.Cycles) ||
		location.pathname.startsWith(AnalyticsRoute.TalentReview) ||
		location.pathname.startsWith(AnalyticsRoute.TreamsScore) ||
		location.pathname.startsWith(AnalyticsRoute.Reward);

	if (
		!props.isEngagementOfficer &&
		!props.hasCycleAnalytics &&
		!props.hasTalentReviewAnalytics &&
		!props.hasTreamsScore
	) {
		return <Separator />;
	}

	const isInTeamAnalytics = !props.isInEngagementAnalytics;

	return (
		<Box marginX="auto" width={'100%'} minWidth={0}>
			<Tabs.Container wide={true}>
				{props.isEngagementOfficer && (
					<Tabs.Item
						title={t('analytics.overview.tab.engagement')}
						selected={props.isInEngagementAnalytics}
						onClick={() => {
							onClickViewTab(ANALYTICS_VIEW_VALUE_ENGAGEMENT);
						}}
					></Tabs.Item>
				)}
				{props.isEngagementOfficer && (
					<Tabs.Item
						title={t('analytics.overview.tab.team')}
						selected={isInTeamAnalytics}
						onClick={() => {
							onClickViewTab(ANALYTICS_VIEW_VALUE_OTHER);
						}}
					></Tabs.Item>
				)}
				{(props.hasCycleAnalytics || props.hasTalentReviewAnalytics || props.hasTreamsScore) && (
					<Tabs.Item title={t('analytics.overview.tab.overview')} path={AnalyticsRoute.Overview} />
				)}
				{props.hasCycleAnalytics && (
					<Tabs.Item
						title={t('analytics.overview.tab.cycleAnalytics')}
						path={AnalyticsRoute.Cycles}
						exact={false}
					/>
				)}
				{props.hasTalentReviewAnalytics && (
					<Tabs.Item title="Talent review & succession" path={AnalyticsRoute.TalentReview} />
				)}
				{props.hasTreamsScore && (
					<Tabs.Item title="Treams Score" path={AnalyticsRoute.TreamsScore} />
				)}
				{props.hasRewards &&
					(props.hasCycleAnalytics || props.hasTalentReviewAnalytics || props.hasTreamsScore) && (
						<Tabs.Item title={t('analytics.overview.tab.reward')} path={AnalyticsRoute.Reward} />
					)}
				{onSnapshotPage && (
					<Flex
						ml="auto"
						alignItems="center"
						alignSelf="center"
						pl={3}
						style={{ gap: tokens.gap.list }}
					>
						<AnalyticsSyncStatus />
						<AnalyticsRefreshButton />
					</Flex>
				)}
			</Tabs.Container>
		</Box>
	);
}

type AnalyticsRoutesProps = {
	hasCycleAnalytics: boolean;
	hasTalentReviewAnalytics: boolean;
	hasTreamsScore: boolean;
	hasRewards: boolean;
	isEngagementOfficer: boolean;
	showEngagementAnalytics: boolean;
	selectableGroupsFilter: (group: graphql.Group) => boolean;
	groupSelection: GroupSelectionData;
	timeMachine: ITimeMachine;
	onChangeTimeMachine: (newTimeMachine: ITimeMachine) => void;
	overview: ReactNode;
};

type AnalyticsRouteConfig = {
	url: string;
	enabled?: boolean;
	render: () => ReactNode;
};

export function AnalyticsRoutes(props: AnalyticsRoutesProps) {
	const headerTabsForSubPages = (
		<AnalyticsHeaderTabs
			isInEngagementAnalytics={false}
			isEngagementOfficer={props.isEngagementOfficer}
			hasCycleAnalytics={props.hasCycleAnalytics}
			hasTalentReviewAnalytics={props.hasTalentReviewAnalytics}
			hasTreamsScore={props.hasTreamsScore}
			hasRewards={props.hasRewards}
		/>
	);

	const routes: AnalyticsRouteConfig[] = [
		{
			// CycleAnalytics reads the cycleTemplateID from the URL via useParams.
			url: `${AnalyticsRoute.Cycles}/:cycleTemplateID?`,
			enabled: props.hasCycleAnalytics,
			render: () => <CycleAnalytics headerFooter={headerTabsForSubPages} />,
		},
		{
			url: AnalyticsRoute.TalentReview,
			enabled: props.hasTalentReviewAnalytics,
			render: () => (
				<TalentReviewAnalytics
					headerFooter={headerTabsForSubPages}
					timeMachine={props.timeMachine}
					onChangeTimeMachine={props.onChangeTimeMachine}
				/>
			),
		},
		{
			url: AnalyticsRoute.TreamsScore,
			enabled: props.hasTreamsScore,
			render: () => <TreamsScoreAnalytics headerFooter={headerTabsForSubPages} />,
		},
		{
			url: AnalyticsRoute.Reward,
			enabled:
				props.hasRewards &&
				(props.hasCycleAnalytics || props.hasTalentReviewAnalytics || props.hasTreamsScore),
			render: () => <RewardAnalytics headerFooter={headerTabsForSubPages} />,
		},
		{
			url: AnalyticsRoute.Metric,
			render: () => (
				<Metric
					groupSelection={props.groupSelection}
					selectableGroupFilter={props.selectableGroupsFilter}
					timeMachine={props.timeMachine}
					onChangeTimeMachine={props.onChangeTimeMachine}
					engagementOfficerMetricsOnly={props.showEngagementAnalytics}
				/>
			),
		},
		{
			url: AnalyticsRoute.Overview,
			render: () => props.overview,
		},
	];

	return (
		<Switch>
			{routes
				.filter((route) => route.enabled ?? true)
				.map((route) => (
					<Route key={route.url} path={route.url}>
						{route.render()}
					</Route>
				))}
		</Switch>
	);
}
